/* How loud the sea is, against what the report says about it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT PART OF THE SPECTRUM
 *
 * The obvious build is a horizontal rule across the frequency strip at 135 dB.
 * It does not work, and the reason is worth writing down so nobody tries it
 * again: the strip auto-ranges. Its floor and ceiling ease towards whatever is
 * arriving, so that a quiet night still fills the picture instead of lying flat
 * along the bottom. A welfare line drawn on a moving axis moves — 135 dB would
 * drift up and down the card, and sit off the top of it most of the night.
 *
 * A level scale has to be nailed down. Same geometry every time you look, so
 * the eye learns where 135 is and reads the bar against it without reading a
 * number. That is a different object from a spectrum, so it is a different
 * object on the page: the strip answers "what is in the sound", this answers
 * "how much of it".
 *
 * ---------------------------------------------------------------------------
 * WHERE THE NUMBER COMES FROM, AND WHY IT IS ALLOWED TO EXIST
 *
 * The analyser sits BEFORE the normalisation gain and before the filters, in
 * both halves of the player. So what it reports is the level in the file, not
 * the level we made comfortable to listen to. That ordering is what makes this
 * component honest and it is load-bearing in three files; moving the analyser
 * after the gain would make every number here wrong by however much the
 * quiet-sea lift happened to be that minute.
 *
 * `offsetDb` is `SPL = dBFS + offsetDb`, summed on the server by
 * app/calibration.py out of three terms that must all be present. Null is the
 * ordinary answer, and when it is null this component renders nothing at all —
 * not a greyed-out scale, not a scale in dBFS. Ground rule 5: uncalibrated
 * means relative, and a welfare scale is the single most dangerous place in
 * this product to break it, because the reader will act on it.
 *
 * ---------------------------------------------------------------------------
 * THE BAND
 *
 * 5-500 Hz, which is what the report measures in, taken from the analyser's own
 * bins as a FRACTION of total power and applied to a true time-domain RMS:
 *
 *     splBand = 20log10(rms) + 10log10(P_band / P_total) + offsetDb
 *
 * Done that way round on purpose. Reconstructing an absolute level from
 * `getFloatFrequencyData` alone means knowing the window's coherent gain and
 * the implementation's normalisation, which is specified loosely enough that it
 * has differed between browsers — and an error there is a few dB, silent, and
 * looks exactly like a real measurement. A ratio of two sums from the same
 * array needs none of that: every constant cancels. The absolute part comes
 * from the time domain, where 20log10(rms) is arithmetic with nothing to get
 * wrong.
 *
 * Note that full scale here is 0 dBFS for a square wave, so a full-scale sine
 * reads -3.01 and not 0. That is the convention the node's `spl_ceiling_db`
 * uses and the two must not drift apart.
 *
 * ---------------------------------------------------------------------------
 * THE CEILING, AND WHY IT IS DRAWN AS A WALL
 *
 * The converter may run out before the report does, and on a node set for
 * gain it does: with a 146 dB ceiling the top of the scale — the range the
 * report associates with injury — is above anything the recorder can
 * represent. A scale showing 175 dB without showing that we cannot see it
 * invites the reading "we are nowhere near that" off a picture that means
 * "we would not know".
 *
 * It is not a fixed property of the fleet, though, and the drawing must not
 * assume it is. Drop the analogue gain far enough and the ceiling climbs past
 * the report's own top mark — at which point the wall lands ON the 175 rather
 * than well short of it, and the hatching shrinks to the sliver above it that
 * is genuinely still out of reach. That is the good case and the picture
 * should read as the good case, so the wall and the mark merge into one line
 * instead of colliding as two.
 *
 * So everything above the ceiling is hatched, and a level that reaches it stops
 * being a number and becomes a bound: `>= 145 dB`, never `145 dB`. The audio
 * that produced it is clipped, its amplitude is unrecoverable and its spectrum
 * is manufactured by the clipping, and the one thing the display must not do is
 * quote it as a measurement.
 */
import { useEffect, useRef } from 'react'
import {
  WELFARE_BAND_HZ, WELFARE_SCALE, WELFARE_ZONES, welfareZone,
} from '../bands'
import { DPR } from '../charts'
import { Fish, type Mood } from './Fish'
import { useT } from '../lang'

/** Bar, then the tick labels under it. */
const HEIGHT = 40
const BAR_TOP = 4
const BAR_H = 16

const UI_FONT = "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif"

/** A sample within one 24-bit LSB of the rail counts as at the rail.
 *
 *  An exact test against 1.0 finds only half of a symmetric clip: two's
 *  complement gives -2^23 exactly, which decodes to -1.0, while +2^23-1 decodes
 *  to 0.99999988 and walks straight past `x >= 1`. The node made precisely this
 *  mistake counting the same thing and reported a third of the damage. */
const RAIL = 1 - 1 / 8_388_608

/* THE THREE STATES, AND THE COLOUR THAT GOES WITH EACH.
 *
 * Read straight off the report's own ranges, which is what makes colouring
 * them defensible at all: HI says "antas ikke a gi betydelige negative
 * konsekvenser" below 135, "kan gi negative konsekvenser" above it, and
 * "horselstap og fysiske skader" for peaks above 175. The colours are those
 * sentences. bands.ts carries the longer argument, including the objection
 * this overrules.
 *
 * 157 is where it goes red rather than 175, because 175 is a PEAK-pressure
 * figure and this bar is an RMS level -- they are not the same quantity, and
 * waiting for 175 on an RMS scale would put the strongest state somewhere the
 * report never said to put it. 157 is the top of the sustained-sound range,
 * which is what this measures. */
const MOOD_AT: { from: number; mood: Mood; ink: string }[] = [
  { from: -Infinity, mood: 'ok', ink: '180, 217, 199' },
  { from: 135, mood: 'warn', ink: '233, 196, 106' },
  { from: 157, mood: 'bad', ink: '230, 57, 38' },
]

const moodAt = (spl: number) => {
  let out = MOOD_AT[0]!
  for (const m of MOOD_AT) if (spl >= m.from) out = m
  return out
}

/** Where a level sits on the fixed scale, 0 at the left edge. */
const pos = (db: number) =>
  Math.max(0, Math.min(1,
    (db - WELFARE_SCALE.lo) / (WELFARE_SCALE.hi - WELFARE_SCALE.lo)))

export function WelfareScale({
  analyser, offsetDb, estimated,
}: {
  /** Null while nothing is playing. The whole component is absent then: a
   *  level meter reading its own floor is indistinguishable from a silent sea,
   *  and this is not the place to make that mistake. */
  analyser: AnalyserNode | null
  /** Null unless every calibration term is present. Null renders nothing. */
  offsetDb: number | null
  /** True when one of the terms came off a datasheet rather than a bench. The
   *  number is still worth showing and it is not a measurement, and the scale
   *  says which without the reader having to know what a term is. */
  estimated: boolean
}) {
  const t = useT()
  const wrap = useRef<HTMLDivElement | null>(null)
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const read = useRef<HTMLSpanElement | null>(null)
  const zoneSay = useRef<HTMLSpanElement | null>(null)
  const offRef = useRef(offsetDb)
  offRef.current = offsetDb

  useEffect(() => {
    const el = canvas.current
    if (!el || !analyser) return
    let raf = 0
    let td: Float32Array | null = null
    let fd: Float32Array | null = null

    /* Eased, and only upward-slowly-downward. A bar tracking every frame at
     * 60 Hz is a flicker rather than a reading; a peak that decays in about
     * three seconds is how every level meter ever built has solved this, and
     * it is the difference between "there was a transient" and "I think I saw
     * something". */
    let shown = WELFARE_SCALE.lo
    let hold = WELFARE_SCALE.lo
    let holdAge = 0
    let lastText = ''
    let lastMood = ''

    const paint = () => {
      raf = requestAnimationFrame(paint)
      const off = offRef.current
      if (off == null || !Number.isFinite(off)) return

      const dpr = DPR()
      const rect = el.getBoundingClientRect()
      const w = Math.max(64, Math.round(rect.width))
      if (el.width !== Math.round(w * dpr) || el.height !== Math.round(HEIGHT * dpr)) {
        el.width = Math.round(w * dpr)
        el.height = Math.round(HEIGHT * dpr)
      }
      const c = el.getContext('2d')
      if (!c) return
      c.setTransform(dpr, 0, 0, dpr, 0, 0)
      c.clearRect(0, 0, w, HEIGHT)

      /* ---- measure ---- */
      const n = analyser.fftSize
      if (!td || td.length !== n) td = new Float32Array(n)
      analyser.getFloatTimeDomainData(td)
      let sum = 0
      let peak = 0
      for (let i = 0; i < n; i++) {
        const x = td[i]!
        sum += x * x
        const a = x < 0 ? -x : x
        if (a > peak) peak = a
      }
      const rms = Math.sqrt(sum / n)
      const clipped = peak >= RAIL

      if (!fd || fd.length !== analyser.frequencyBinCount) {
        fd = new Float32Array(analyser.frequencyBinCount)
      }
      analyser.getFloatFrequencyData(fd)
      const perBin = (analyser.context.sampleRate / 2) / fd.length
      let pTot = 0
      let pBand = 0
      for (let i = 1; i < fd.length; i++) {
        const v = fd[i]!
        if (!Number.isFinite(v)) continue
        const p = Math.pow(10, v / 10)
        pTot += p
        const hz = i * perBin
        if (hz >= WELFARE_BAND_HZ.lo && hz <= WELFARE_BAND_HZ.hi) pBand += p
      }
      // Every normalisation constant cancels in the ratio; see the header.
      const frac = pTot > 0 ? pBand / pTot : 0
      const spl = rms > 0 && frac > 0
        ? 20 * Math.log10(rms) + 10 * Math.log10(frac) + off
        : WELFARE_SCALE.lo

      // Clipping is a property of the whole waveform, not of the 5-500 Hz part
      // of it, so a clipped frame pins the bar at the ceiling whatever the
      // band arithmetic came out at. The number stops being a level and starts
      // being a floor, which is what the readout below says.
      const now = clipped ? off : spl
      shown += (now - shown) * (now > shown ? 0.35 : 0.08)
      if (now >= hold) { hold = now; holdAge = 0 } else if (++holdAge > 180) {
        hold += (now - hold) * 0.06
      }

      /* ---- the zones ---- */
      for (let z = 0; z < WELFARE_ZONES.length; z++) {
        const from = z === 0 ? WELFARE_SCALE.lo : WELFARE_ZONES[z]!.from
        const to = z + 1 < WELFARE_ZONES.length
          ? WELFARE_ZONES[z + 1]!.from : WELFARE_SCALE.hi
        const zx = pos(from) * w
        const zw = pos(to) * w - zx
        // Weight, not hue. A green-amber-red ramp turns a range into a verdict
        // at a glance, and this system is not entitled to a verdict -- the
        // report it comes from says thresholds for injury are not known.
        c.fillStyle = `rgba(147, 171, 159, ${0.05 + z * 0.045})`
        c.fillRect(zx, BAR_TOP, Math.max(0, zw), BAR_H)
      }

      /* ---- the bar, in the colour of wherever its tip has reached ---- */
      const m = moodAt(clipped ? off : spl)
      const bw = pos(shown) * w
      c.fillStyle = `rgba(${m.ink}, ${estimated ? 0.62 : 0.9})`
      c.fillRect(0, BAR_TOP, bw, BAR_H)

      // Peak hold: a hairline where the loudest recent frame reached.
      if (hold > WELFARE_SCALE.lo + 1) {
        c.fillStyle = 'rgba(214, 240, 227, 0.85)'
        c.fillRect(Math.min(w - 1, pos(hold) * w), BAR_TOP, 1.5, BAR_H)
      }

      /* ---- beyond the instrument ---- */
      const cx = pos(off) * w
      if (cx < w) {
        c.save()
        c.beginPath()
        c.rect(cx, BAR_TOP, w - cx, BAR_H)
        c.clip()
        c.strokeStyle = 'rgba(147, 171, 159, 0.30)'
        c.lineWidth = 1
        c.beginPath()
        for (let x = cx - BAR_H; x < w + BAR_H; x += 5) {
          c.moveTo(x, BAR_TOP + BAR_H)
          c.lineTo(x + BAR_H, BAR_TOP)
        }
        c.stroke()
        c.restore()
        c.strokeStyle = 'rgba(147, 171, 159, 0.75)'
        c.lineWidth = 1
        c.beginPath()
        c.moveTo(Math.round(cx) + 0.5, BAR_TOP)
        c.lineTo(Math.round(cx) + 0.5, BAR_TOP + BAR_H)
        c.stroke()
      }

      /* ---- the edges, and their numbers ---- */
      c.font = '11px ' + UI_FONT
      c.textBaseline = 'top'
      c.textAlign = 'center'
      for (const z of WELFARE_ZONES) {
        if (!z.edge) continue
        /* A converter with enough headroom lands its ceiling on top of one of
         * the report's marks -- p-002 clips at 175.8, which is the 175 plus a
         * fifth of a pixel-worth of decibel. Drawn naively that is two rules a
         * few pixels apart with a number squeezed under one of them, and it
         * reads as a rendering fault rather than as two facts that nearly
         * coincide.
         *
         * They merge, and the threshold is the label's own half-width rather
         * than a fixed number of pixels: the question is not how far apart the
         * two levels are, it is whether a reader can tell the two marks apart,
         * and that is decided by how wide `175` is drawn.
         *
         * Merging snaps to the CEILING's x, not the mark's, so the hatching
         * starts exactly on the line instead of leaving an unhatched slice
         * that means nothing. That draws the report's mark up to a decibel out
         * of place -- which is a fifth of the +/-4 dB the sensitivity term is
         * quoted to, so the picture is not claiming a precision it loses here.
         * The mark keeps its number, because the number is what says where the
         * instrument stops. */
        const ex = pos(z.from) * w
        const onCeiling = cx < w
          && Math.abs(ex - cx) < c.measureText(z.edge).width / 2
        const x = onCeiling ? cx : ex
        if (x <= 0 || x >= w) continue
        c.strokeStyle = onCeiling
          ? 'rgba(147, 171, 159, 0.75)'
          : 'rgba(147, 171, 159, 0.55)'
        c.lineWidth = 1
        c.beginPath()
        c.moveTo(Math.round(x) + 0.5, BAR_TOP)
        c.lineTo(Math.round(x) + 0.5, BAR_TOP + BAR_H + 3)
        c.stroke()
        c.fillStyle = '#93ab9f'
        c.fillText(z.edge, x, BAR_TOP + BAR_H + 5)
      }

      /* ---- the readout, written straight to the DOM ---- */
      const shownSpl = clipped ? off : spl
      const text = `${clipped ? '≥ ' : ''}${Math.round(shownSpl)}`
      if (m.mood !== lastMood) {
        lastMood = m.mood
        // One attribute; the stylesheet colours the number and picks the fish.
        // Setting either from React would re-render the page at 60 Hz to move
        // a hue.
        if (wrap.current) wrap.current.dataset.mood = m.mood
      }
      if (text !== lastText) {
        lastText = text
        if (read.current) read.current.textContent = text
        /* A bound gets a sentence about being a bound, not the sentence for
         * the zone it lands in. `>= 145` sits in the 135-157 range, and
         * printing that range's "may have negative effects" reads as a finding
         * about the sound -- when the actual position is that the sound went
         * off the top of the instrument and could be anywhere above. Quoting
         * the lower zone in that situation understates, with authority. */
        if (zoneSay.current) {
          zoneSay.current.textContent = clipped
            ? t('The sound went above what this listening post can measure. It '
              + 'was at least this loud; how much louder is not known.')
            : t(welfareZone(shownSpl).says)
        }
      }
    }

    raf = requestAnimationFrame(paint)
    return () => cancelAnimationFrame(raf)
    // `estimated` is read inside the loop; it changes only when the mission
    // does, and re-running the effect then is correct.
  }, [analyser, estimated, t])

  if (!analyser || offsetDb == null || !Number.isFinite(offsetDb)) return null

  return (
    <div className="lvl" ref={wrap} data-mood="ok">
      <div className="lvl-head">
        {/* Three drawn, one shown. Swapping which by CSS keeps the whole
            component out of React's render path, which is what lets the scale
            update at frame rate without touching the page. */}
        <span className="lvl-fish">
          <Fish mood="ok" />
          <Fish mood="warn" />
          <Fish mood="bad" />
        </span>
        <span className="lvl-now">
          <span ref={read}>—</span>
          <span className="lvl-unit">dB re 1 µPa</span>
        </span>
        {estimated && <span className="lvl-est">{t('estimate')}</span>}
      </div>
      {/* No caption. Three sentences under the bar was the most text on the
          page, directly in the way of the listening it is meant to annotate,
          and a reader who is here to hear the sea does not want a literature
          review first.

          The caveat has not been dropped -- it cannot be, since the numbers on
          this scale are the report's and the report is explicit that they are
          not limits. It has moved to the aside, beside the note about levels
          being estimates, which is where a person goes when they want to know
          what a number means rather than what it is. `zoneSay` is now the
          canvas's description, so the sentence still reaches anyone using a
          screen reader -- who has no other way to read a bar. */}
      <canvas
        ref={canvas}
        role="img"
        aria-label={t('Sound level against the ranges published by the '
          + 'Institute of Marine Research')}
        style={{ width: '100%', height: HEIGHT }}
      />
      <span ref={zoneSay} className="sr-only" />
    </div>
  )
}
